<template>
  <Teleport to="body">
    <div class="loading-overlay" role="status" aria-live="polite">
      <div class="loading-card">
        <p v-if="query" class="search-query">"{{ query }}"</p>
        <loading-spinner
          stroke-color="var(--colour-orange)"
          location="ai-search"
          class="large-spinner"
        />
        <transition name="message-fade" mode="out-in">
          <p :key="currentMessage" class="loading-message">
            {{ currentMessage }}
          </p>
        </transition>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'

defineProps<{
  query?: string
}>()

const { t } = useI18n()

const loadingMessageKeys = [
  'Searching the great outdoors...',
  'Finding your holiday...',
  'Exploring hidden gems...',
  'Counting the stars...',
  'Warming up the campfire...',
]

const currentIndex = ref(0)
const currentMessage = computed(() => t(loadingMessageKeys[currentIndex.value]))

let intervalId: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  intervalId = setInterval(() => {
    currentIndex.value = (currentIndex.value + 1) % loadingMessageKeys.length
  }, 3000)
})

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId)
  }
})
</script>

<style scoped>
.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-card {
  background: white;
  padding: 2em 1.5em;
  border-radius: 1em;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 90vw;
  max-width: 400px;
  writing-mode: horizontal-tb;
  direction: ltr;
}

.large-spinner {
  width: 2em;
  height: 2em;
}

.search-query {
  font-size: 1.1em;
  color: var(--colour-dark-blue);
  font-style: italic;
  margin: 0 0 1.5em;
  padding: 0;
  word-break: normal;
  white-space: normal;
  overflow-wrap: break-word;
  line-height: 1.4;
  width: 100%;
}

.loading-message {
  margin-top: 1.5em;
  font-size: 1em;
  color: var(--colour-dark-blue);
  min-height: 1.5em;
  white-space: normal;
  width: 100%;
}

.message-fade-enter-active,
.message-fade-leave-active {
  transition: opacity 0.4s ease;
}

.message-fade-enter-from,
.message-fade-leave-to {
  opacity: 0;
}
@media screen and (min-width: 768px) {
  .loading-overlay {
    overflow-y: auto;
    padding: 20px;
  }

  .loading-card {
    max-width: min(600px, 90vw);
    padding: 40px 48px;
    box-sizing: border-box;
  }

  .search-query {
    font-size: 24px;
    line-height: 1.4;
    margin-bottom: 32px;
    word-break: normal;
    overflow-wrap: break-word;
    hyphens: none;
  }

  .large-spinner {
    width: 60px;
    height: 60px;
  }

  .loading-message {
    font-size: 18px;
    line-height: 1.5;
    margin-top: 24px;
  }
}
</style>
